import React from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { amountDisplay, isDarkModeOn } from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';

const AnalyticsChart = ({ fullIncomeExpenseStatistics, months, performanceMode = {} }) => {
    const { language, t } = useLanguage();
    const isCompact = Boolean(performanceMode.compact);
    const disableAnimation = Boolean(performanceMode.disableAnimation);
    const chartHeight = isCompact ? 250 : 380;
    const incomes = fullIncomeExpenseStatistics.map((item) => item.total_income);
    const expenses = fullIncomeExpenseStatistics.map((item) => item.total_expenses);
    const isDark = isDarkModeOn();
    const labelColor = isDark ? '#9ba3aa' : '#958f9f';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(149,143,159,0.18)';

    const series = [
        { name: t('income'), data: incomes },
        { name: t('expense'), data: expenses },
    ];

    const categories = months.map((date) => dayjs(date).format('MMM'));

    const options = {
        chart: {
            type: 'bar',
            height: 380,
            stacked: true,
            fontFamily: 'Lato, sans-serif',
            background: 'transparent',
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: {
                enabled: !disableAnimation,
                easing: 'easeinout',
                speed: disableAnimation ? 1 : 450,
                animateGradually: {
                    enabled: !disableAnimation,
                    delay: disableAnimation ? 0 : 60,
                },
            },
        },
        colors: ['#11a679', '#fa3131'],
        plotOptions: {
            bar: {
                borderRadius: 6,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
                columnWidth: '28%',
            },
        },
        dataLabels: { enabled: false },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '12px',
            labels: { colors: labelColor },
            markers: { radius: 12 },
            itemMargin: { horizontal: 8 },
        },
        grid: {
            show: true,
            strokeDashArray: 4,
            borderColor: gridColor,
            xaxis: {
                lines: { show: false },
            },
            padding: { left: 6, right: 6 },
        },
        xaxis: {
            categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                style: {
                    fontSize: '11px',
                    colors: labelColor,
                },
            },
        },
        yaxis: {
            labels: {
                formatter: (value) => `${amountDisplay(value, language)}`,
                style: {
                    fontSize: '10px',
                    colors: labelColor,
                },
            },
            tickAmount: 4,
        },
        fill: {
            opacity: 1,
        },
        states: {
            hover: { filter: { type: 'lighten', value: 0.06 } },
            active: { filter: { type: 'darken', value: 0.08 } },
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            shared: true,
            intersect: false,
            y: {
                formatter: (val) => `৳${amountDisplay(Math.abs(val), language)}`,
            },
        },
        responsive: [
            {
                breakpoint: 992,
                options: {
                    chart: { height: 340 },
                    plotOptions: {
                        bar: { columnWidth: '35%', borderRadius: 5 },
                    },
                    xaxis: {
                        labels: { style: { fontSize: '10px' } },
                    },
                    yaxis: {
                        tickAmount: 4,
                        labels: {
                            formatter: (value) => `${amountDisplay(value, language)}`,
                            style: { fontSize: '10px' },
                        },
                    },
                },
            },
            {
                breakpoint: 768,
                options: {
                    chart: { height: 270 },
                    plotOptions: {
                        bar: { columnWidth: '40%', borderRadius: 5 },
                    },
                    legend: { fontSize: '11px' },
                    xaxis: {
                        labels: { style: { fontSize: '9px' } },
                    },
                    yaxis: {
                        tickAmount: 3,
                        labels: {
                            formatter: (value) => `${amountDisplay(value, language)}`,
                            style: { fontSize: '9px' },
                        },
                    },
                    grid: { padding: { left: 2, right: 2 } },
                },
            },
            {
                breakpoint: 576,
                options: {
                    chart: { height: 240 },
                    plotOptions: {
                        bar: { columnWidth: '48%', borderRadius: 4 },
                    },
                    legend: { fontSize: '11px' },
                    xaxis: {
                        labels: { style: { fontSize: '9px' } },
                    },
                    yaxis: {
                        tickAmount: 4,
                        labels: {
                            formatter: (value) => `${amountDisplay(value, language)}`,
                            style: { fontSize: '9px' },
                        },
                    },
                    grid: { padding: { left: 2, right: 2 } },
                },
            },
        ],
    };

    return (
        <section>
            <div className="custom-container">
                <div className="chart">
                    <div className="d-flex justify-content-between">
                        <h3 className="fw-semibold dark-text">
                            {t('analytics')} |{' '}
                            <span className="light-text fw-normal">{`${categories[0]} - ${categories[categories.length - 1]}`}</span>
                        </h3>
                        <ul className="listing">
                            <li>W</li>
                            <li>M</li>
                            <li className="active">Y</li>
                        </ul>
                    </div>
                    <Chart options={options} series={series} type="bar" height={chartHeight} />
                </div>
            </div>
        </section>
    );
};

export default AnalyticsChart;
